<template>
    <div
        id="productCustomisationCanvas"
        ref="productCustomisationCanvas"
        class="w-full h-auto aspect-square rounded-lg overflow-hidden"
    >
        <v-stage
            ref="stage"
            :config="canvasConfig"
        >
            <v-layer>
                <v-image :config="imageConfig" />
                <v-image
                    v-for="(layerImage, index) in layerImages"
                    :key="`level-${layerImage.layer_position}-${index}`"
                    :config="{ image: layerImage.image, width: width, height: height, offsetX: layerImage.offset }"
                />
                <v-text
                    v-if="textVariation.text_is_editable && !textVariation.customer_image_enabled"
                    :config="textConfig"
                />
                <template v-if="textVariation.customer_image_enabled">
                    <v-circle
                        v-if="textVariation.customer_image_angle === 360"
                        :config="customerImageCircle"
                    />
                    <v-arc
                        v-else
                        :config="customerImageArc"
                    />
                </template>
                <v-text-path
                    v-if="textVariation.customer_image_enabled"
                    :config="topTextPathConfig"
                />
                <v-text-path
                    v-if="textVariation.customer_image_enabled"
                    :config="bottomTextPathConfig"
                />
            </v-layer>
        </v-stage>
    </div>
</template>

<script>
import debounce from 'debounce';
import { mapGetters } from 'vuex';

export default {
    name: 'ProductCustomisationCanvas',
    props: {
        width: {
            type: Number,
            default: 500,
        },
        height: {
            type: Number,
            default: 500,
        },
    },
    data() {
        return {
            canvas: {},
            canvasWidth: 500,
            customerImageObject: null,
            imageObject: null,
            layerImages: [],
            textHeight: 300,
        };
    },
    computed: {
        // Vuex
        ...mapGetters('product', [
            'text',
            'textLine2',
            'fontFamily',
            'customerImageUrl',
            'customerImageHeight',
            'customerImageWidth',
            'customerImagePreview',
            'textVariation',
            'customisationImage',
            'customisationLayers',
        ]),

        imageUrl() {
            return this.customisationImage;
        },

        sharedTextConfig() {
            return {
                align: 'center',
                fontFamily: this.fontFamily,
                fontSize: Number(this.textVariation.text_size),
                fillPriority: 'linear-gradient',
                fillLinearGradientStartPoint: { x: 0, y: 100 },
                fillLinearGradientEndPoint: { x: Number(this.textVariation.text_width), y: 100 },
                fillLinearGradientColorStops: this.fillGradientColorStops,
                strokeLinearGradientStartPoint: { x: 0, y: 100 },
                strokeLinearGradientEndPoint: { x: Number(this.textVariation.text_width), y: 100 },
                strokeLinearGradientColorStops: this.strokeGradientColorStops,
                letterSpacing: Number(this.textVariation.text_letter_spacing),
                lineHeight: this.lineHeight,
                lineJoin: 'round',
                fill: this.textVariation.text_colour,
                fillAfterStrokeEnabled: true,
                strokeWidth: Number(this.textVariation.text_stroke_width),
                stroke: this.textVariation.text_stroke_color,
            };
        },
        textConfig() {
            return {
                ...this.sharedTextConfig,
                text: this.text,
                verticalAlign: 'middle',
                width: Number(this.textVariation.text_width),
                height: this.textHeight,
                offsetX: this.textVariation.text_width / 2,
                offsetY: this.textHeight / 2,
                x: this.textXOffset,
                y: this.textYOffset,
            };
        },
        textPathConfig() {
            return {
                ...this.sharedTextConfig,
                x: Number(this.textVariation.customer_image_x_offset),
                y: Number(this.textVariation.customer_image_y_offset),
                verticalAlign: 'middle',
            };
        },
        topTextPathConfig() {
            return {
                ...this.textPathConfig,
                text: this.text || 'Line 1',
                data: this.topTextPathData,
                textBaseline: 'top',
            };
        },
        bottomTextPathConfig() {
            return {
                ...this.textPathConfig,
                text: this.textLine2 || (this.text ? '' : 'Line 2'),
                data: this.bottomTextPathData,
                textBaseline: 'bottom',
            };
        },
        topTextPathData() {
            const radius = Number(this.textVariation.customer_image_radius) * 0.95;
            return `M 0 ${radius} a ${radius} ${radius} 0 1 1 1 0`;
        },
        bottomTextPathData() {
            const radius = Number(this.textVariation.customer_image_radius) * 0.95;
            return `M 0 -${radius} a ${radius} ${radius} 0 1 0 1 0`;
        },
        canvasConfig() {
            return {
                width: this.width,
                height: this.height,
                scaleX: this.scale,
                scaleY: this.scale,
            };
        },
        imageConfig() {
            return {
                image: this.imageObject,
                width: this.width,
                height: this.height,
            };
        },
        customerImageCircle() {
            const config = {
                x: Number(this.textVariation.customer_image_x_offset),
                y: Number(this.textVariation.customer_image_y_offset),
                radius: Number(this.textVariation.customer_image_radius),
            };

            if (this.customerImageObject) {
                return {
                    ...config,
                    fillPatternImage: this.customerImageObject,
                    fillPatternOffset: { x: Number(this.textVariation.customer_image_radius), y: Number(this.textVariation.customer_image_radius) },
                    fillPatternScaleX: (Number(this.textVariation.customer_image_radius) * 2) / this.customerImageWidth,
                    fillPatternScaleY: (Number(this.textVariation.customer_image_radius) * 2) / this.customerImageHeight,
                };
            }

            return {
                ...config,
                fill: 'rgba(0,0,0,0.1)',
            };
        },
        customerImageArc() {
            const config = {
                x: Number(this.textVariation.customer_image_x_offset),
                y: Number(this.textVariation.customer_image_y_offset),
                angle: Number(this.textVariation.customer_image_angle),
                outerRadius: Number(this.textVariation.customer_image_radius),
                innerRadius: 0,
                rotation: Number(this.textVariation.customer_image_rotation),
            };

            if (this.customerImageObject) {
                return {
                    ...config,
                    fillPatternImage: this.customerImageObject,
                    fillPatternOffset: { x: Number(this.textVariation.customer_image_radius), y: Number(this.textVariation.customer_image_radius) },
                    fillPatternScaleX: (Number(this.textVariation.customer_image_radius) * 2) / this.customerImageWidth,
                    fillPatternScaleY: (Number(this.textVariation.customer_image_radius) * 2) / this.customerImageHeight,
                    fillPatternRotation: -Number(this.textVariation.customer_image_rotation),
                };
            }

            return {
                ...config,
                fill: 'rgba(0,0,0,0.1)',
            };
        },
        textXOffset() {
            return this.width * (this.textVariation.text_x_offset / 100);
        },
        textYOffset() {
            return this.height * (this.textVariation.text_y_offset / 100);
        },
        lineHeight() {
            return Number(this.textVariation.text_line_height) / 100;
        },
        scale() {
            const scale = this.canvasWidth / this.width;
            return Math.min(scale, 1);
        },
        gradientColorStops() {
            if (this.textVariation.gradient && this.textVariation.gradient.length > 0) {
                const stops = [];

                this.textVariation.gradient.forEach((stop) => {
                    stops.push(stop.position);
                    stops.push(stop.colour);
                });

                return stops;
            }

            return null;
        },
        fillGradientColorStops() {
            if (!this.textVariation.text_gradient_stroke_only) {
                return this.gradientColorStops;
            }

            return null;
        },
        strokeGradientColorStops() {
            if (this.textVariation.text_gradient_stroke_only) {
                return this.gradientColorStops;
            }

            return null;
        },
    },
    watch: {
        customerImageUrl() {
            this.updateCustomerImage();
        },
        customerImagePreview() {
            this.updateCustomerImage();
        },
        customisationImage(value) {
            this.updateCustomisationImage(value);
        },
        customisationLayers() {
            this.updateLayerImages();
        },
        text() {
            this.updateCanvas();
        },
        textLine2() {
            this.updateCanvas();
        },
    },
    mounted() {
        setTimeout(() => {
            if (this.$refs.productCustomisationCanvas) {
                this.canvasWidth = this.$refs.productCustomisationCanvas.clientWidth;
                this.canvas = this.$refs.productCustomisationCanvas;
            }
        }, 1);

        this.updateCustomisationImage();
        this.updateCustomerImage();
        this.updateLayerImages();
    },
    methods: {
        updateCanvas: debounce(function _() {
            if (this.$refs.stage) {
                this.$emit('change', this.$refs.stage.getStage().toDataURL());
            }
        }, 1000),
        updateCustomisationImage() {
            const image = new window.Image();
            image.src = this.$store.getters['product/customisationImage'];
            image.origin = 'anonymous';
            image.crossOrigin = 'Anonymous';
            image.onload = () => {
                this.imageObject = image;
                this.updateCanvas();
            };
        },
        updateCustomerImage() {
            const image = new window.Image();
            image.src = this.$store.getters['product/customerImagePreview'];
            image.origin = 'anonymous';
            image.crossOrigin = 'Anonymous';
            image.onload = () => {
                this.customerImageObject = image;
                this.updateCanvas();
            };
        },
        updateLayerImages() {
            this.layerImages = [];

            this.$store.getters['product/customisationLayers'].forEach((layer) => {
                const image = new window.Image();
                image.src = layer.image;
                image.origin = 'anonymous';
                image.crossOrigin = 'Anonymous';
                image.onload = () => {
                    this.layerImages.push({
                        layer_position: layer.layer_position,
                        image,
                        offset: layer.offset,
                    });
                    this.updateCanvas();

                    this.layerImages.sort((a, b) => a.layer_position - b.layer_position);
                };
            });
        },
    },
};
</script>

<style>

</style>
